// src/components/molecules/ConfirmModal/ConfirmModal.tsx
import React from 'react';
import { Container, Row, Col, Button } from 'shared-portal-component';

import AppModal from '@/components/common/AppModal/AppModal';

export interface ConfirmModalProps {
  /**
   * Whether the modal is open and rendered.
   */
  open: boolean;

  /**
   * Callback invoked when the modal should close.
   */
  onClose: () => void;

  /**
   * Main heading shown at the top of the modal.
   */
  title: string | React.ReactNode;

  /**
   * Array of paragraph(s) to render as body text inside the modal.
   */
  paragraphs: Array<string | React.ReactNode>;

  /**
   * Called when the user clicks the Confirm button.
   */
  onConfirm: () => void;

  /**
   * Optional button label for the Confirm button. Defaults to `"Confirm"`.
   */
  confirmText?: string;

  /**
   * Optional modal size mapping (lg=8, md=6, sm=4). Forwarded to `Modal`.
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether the confirm button should be disabled.
   * Useful for cases like submitting state.
   */
  confirmDisabled?: boolean;

  /**
   * Optional slot for extra content below the body paragraphs
   * (e.g., warnings, links, or custom JSX).
   */
  footerSlot?: React.ReactNode;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  title,
  paragraphs,
  onConfirm,
  confirmText = 'Confirm',
  size = 'lg',
  confirmDisabled = false,
  footerSlot,
}) => {
  if (!open) return null;

  return (
    <AppModal onClose={onClose} title={title} size={size}>
      <Container fluid>
        <Row spacing="md">
          <Col size={12}>
            {paragraphs.map((paragraph, idx) => (
              <p key={idx} className="mb-4">
                {paragraph}
              </p>
            ))}
            {footerSlot}
          </Col>
        </Row>

        <Row spacing="none" justify="center">
          <Col size="content">
            <Button
              variant="primary"
              size="md"
              onClick={onConfirm}
              disabled={confirmDisabled}
              aria-label={typeof confirmText === 'string' ? confirmText : 'Confirm'}
              text={confirmText}
            />
          </Col>
        </Row>
      </Container>
    </AppModal>
  );
};

export default ConfirmModal;
